import { ILaya } from "./ILaya";
import { Stage } from "./laya/display/Stage";
import { InputManager } from "./laya/events/InputManager";
import { Loader } from "./laya/net/Loader";
import { Render } from "./laya/renders/Render";
import { Browser } from "./laya/utils/Browser";
import { Timer } from "./laya/utils/Timer";
import { LayaEnv } from "./LayaEnv";
import { Config } from "./Config";
import { Shader3D } from "./laya/RenderEngine/RenderShader/Shader3D";
import { LayaGL } from "./laya/layagl/LayaGL";
import { Material } from "./laya/resource/Material";
import { VertexElementFormat } from "./laya/renders/VertexElementFormat";
import { VertexMesh } from "./laya/RenderEngine/RenderShader/VertexMesh";
import { BaseRenderNode2D } from "./laya/NodeRender2D/BaseRenderNode2D";
import { Texture2D } from "./laya/resource/Texture2D";
import { Texture2DArray } from "./laya/resource/Texture2DArray";
import { TextureCube } from "./laya/resource/TextureCube";
import { HalfFloatUtils } from "./laya/utils/HalfFloatUtils";
import { BlendModeHandler } from "./laya/webgl/canvas/BlendMode";
import { Shader2D } from "./laya/webgl/shader/d2/Shader2D";
import { ShaderDefines2D } from "./laya/webgl/shader/d2/ShaderDefines2D";
import { HTMLCanvas } from "./laya/resource/HTMLCanvas";
import { PAL } from "./laya/platform/PlatformAdapters";
import { SoundManager } from "./laya/media/SoundManager";
import { GraphicsMesh } from "./laya/webgl/utils/GraphicsMesh";
import { Mesh2DRender } from "./laya/display/Scene2DSpecial/Mesh2DRender";
import { PostProcess2D } from "./laya/display/PostProcess2D";
import { Render2DProcessor } from "./laya/display/Render2DProcessor";
import { Blit2DCMD } from "./laya/display/Scene2DSpecial/RenderCMD2D/Blit2DCMD";
import { Scene } from "./laya/display/Scene";
export class Laya {
    static init(...args) {
        if (Laya._inited)
            return Promise.resolve();
        Laya._inited = true;
        let stageConfig;
        if (typeof (args[0]) === "number") {
            stageConfig = {
                designWidth: args[0],
                designHeight: args[1]
            };
        }
        else
            stageConfig = args[0];
        ILaya.systemTimer = Laya.systemTimer = Timer.gSysTimer = systemTimer = new Timer(false);
        ILaya.timer = Laya.timer = timer = new Timer(false);
        ILaya.physicsTimer = Laya.physicsTimer = physicsTimer = new Timer(false);
        ILaya.loader = Laya.loader = loader = new Loader();
        PAL.__init__();
        let steps = [];
        steps.push(() => {
            let mainCanvas = Browser.mainCanvas = new HTMLCanvas(false);
            mainCanvas.source = PAL.browser.createMainCanvas();
            Browser.canvas = new HTMLCanvas(true);
            Browser.context = Browser.canvas.context;
            return PAL.browser.start();
        });
        if (LayaEnv.beforeInit)
            steps.push(() => LayaEnv.beforeInit(stageConfig));
        Laya._beforeInitCallbacks.forEach(func => steps.push(() => func(stageConfig)));
        steps.push(() => LayaGL.renderDeviceFactory.createEngine(null, Browser.mainCanvas));
        steps.push(() => Laya.initRender2D(stageConfig));
        let laya3D = window["Laya3D"];
        if (laya3D)
            steps.push(() => laya3D.__init__());
        steps.push(() => Promise.all(Laya._initCallbacks.map(func => func())));
        steps.push(() => {
            let p = Promise.resolve();
            for (let func of Laya._afterInitCallbacks)
                p = p.then(func);
            return p;
        });
        if (LayaEnv.afterInit)
            steps.push(() => LayaEnv.afterInit());
        let p = Promise.resolve();
        for (let step of steps)
            p = p.then(step);
        return p;
    }
    static initRender2D(stageConfig) {
        PAL.browser.onInitRender();
        stage = window.stage = ILaya.stage = Laya.stage = new Stage();
        stage.size(stageConfig.designWidth, stageConfig.designHeight);
        if (stageConfig.scaleMode)
            stage.scaleMode = stageConfig.scaleMode;
        if (stageConfig.screenMode)
            stage.screenMode = stageConfig.screenMode;
        if (stageConfig.alignV)
            stage.alignV = stageConfig.alignV;
        if (stageConfig.alignH)
            stage.alignH = stageConfig.alignH;
        if (Config.isAlpha)
            stage.bgColor = "#00000000";
        else if (stageConfig.backgroundColor)
            stage.bgColor = stageConfig.backgroundColor;
        VertexElementFormat.__init__();
        VertexMesh.__init__();
        Shader3D.init();
        GraphicsMesh.__init__();
        ShaderDefines2D.__init__();
        Render.__init__();
        Shader2D.__init__();
        BlendModeHandler._init_();
        Texture2D.__init__();
        TextureCube.__init__();
        Texture2DArray.__init__();
        HalfFloatUtils.__init__();
        Scene.__init__();
        Render2DProcessor.__init__();
        BaseRenderNode2D.initBaseRender2DCommandEncoder();
        Blit2DCMD.__init__();
        PostProcess2D.init();
        Material.__initDefine__();
        Mesh2DRender.__init__();
        InputManager.__init__();
        SoundManager.__init__();
    }
    static alertGlobalError(value) {
        if (value)
            PAL.browser.captureGlobalError(Laya._onGlobalError);
        else
            PAL.browser.captureGlobalError(null);
    }
    static _onGlobalError(ev) {
        var _a;
        let msg = "Something went wrong\n"
            + (ev.message || ev.reason)
            + "\n"
            + (ev.stack || ((_a = ev.error) === null || _a === void 0 ? void 0 : _a.stack));
        if (_erralert++ < 5)
            PAL.browser.alert(msg);
        else
            console.error(msg);
    }
    static addInitCallback(callback) {
        Laya._initCallbacks.push(callback);
    }
    static addBeforeInitCallback(callback) {
        Laya._beforeInitCallbacks.push(callback);
    }
    static addAfterInitCallback(callback) {
        Laya._afterInitCallbacks.push(callback);
    }
    static importNative(name) {
        if (!LayaEnv.isConch)
            return null;
        let path = window.$DLL_PATHS[name];
        let obj = window.importNative(path || name);
        if (!obj)
            throw new Error(`failed to load ${name}`);
        return obj;
    }
}
Laya.stage = null;
Laya.systemTimer = null;
Laya.physicsTimer = null;
Laya.timer = null;
Laya.loader = null;
Laya._inited = false;
Laya._initCallbacks = [];
Laya._beforeInitCallbacks = [];
Laya._afterInitCallbacks = [];
var _erralert = 0;
ILaya.Laya = Laya;
ILaya.Loader = Loader;
ILaya.InputManager = InputManager;
export var init = Laya.init;
export var stage;
export var systemTimer;
export var physicsTimer;
export var timer;
export var loader;
export var alertGlobalError = Laya.alertGlobalError;
export var addInitCallback = Laya.addInitCallback;
export var addBeforeInitCallback = Laya.addBeforeInitCallback;
export var addAfterInitCallback = Laya.addAfterInitCallback;
export var importNative = Laya.importNative;
